import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, Phone, Lock, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_c9a69f8c-f09a-4a22-b109-5dc5ad9f5a15/artifacts/qtk9sn35_MEDX-AI-Welcome-Poster.pptx%20%281%29.png";

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login, register, verifyOtp } = useAuth();
  
  const [formData, setFormData] = useState({
    mobile: '',
    passcode: '',
    acceptedTerms: false
  });
  const [loading, setLoading] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [fallbackCode, setFallbackCode] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'passcode' && value.length > 4) return;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.acceptedTerms) {
      toast.error('Please accept the Terms & Conditions');
      return;
    }

    if (formData.passcode.length !== 4) {
      toast.error('Passcode must be 4 digits');
      return;
    }

    setLoading(true);
    try {
      // Try login first
      await login(formData.mobile, formData.passcode);
      
      // Login success - now send OTP for verification
      try {
        const api = (await import('@/context/AuthContext')).default;
      } catch {}
      
      // Request OTP via the API
      const axios = (await import('axios')).default;
      const API_URL = 'https://admin.afriperx.online';
      const otpRes = await axios.post(`${API_URL}/api/auth/request-login-otp`, {
        mobile: formData.mobile
      });
      
      setMaskedEmail(otpRes.data?.email_masked || '');
      if (otpRes.data?.otp_fallback) {
        setFallbackCode(otpRes.data.otp_fallback);
      } else {
        setFallbackCode('');
      }
      setIsNewUser(false);
      setShowOtpModal(true);
      toast.success(otpRes.data?.message || 'OTP sent');
    } catch (error) {
      // If login fails, user might not exist - show error
      toast.error(error.response?.data?.detail || 'Invalid mobile number or passcode');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      // Verify OTP
      const axios = (await import('axios')).default;
      const API_URL = 'https://admin.afriperx.online';
      await axios.post(`${API_URL}/api/auth/verify-login-otp`, {
        mobile: formData.mobile,
        otp: otp
      });
      
      toast.success('Welcome to AfriPerx!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDF8F8] via-[#FFF5F5] to-[#FDF8F8] relative overflow-hidden">
      <div className="absolute inset-0 hero-glow opacity-50" />
      
      <div className="relative z-10 min-h-screen flex flex-col px-6 py-8">
        <button 
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors mb-8"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-5 h-5" />
          Back
        </button>

        <div className="flex-1 flex flex-col items-center justify-center max-w-md mx-auto w-full">
          <div className="mb-8 inline-block" data-testid="login-logo">
            <Link to="/">
              <img src={LOGO_URL} alt="AfriPerx" className="h-14 w-auto" />
            </Link>
          </div>

          <h1 className="font-syne text-3xl md:text-4xl font-bold text-gray-900 mb-2 text-center">
            Sign Into Your Account
          </h1>
          <p className="text-gray-500 mb-8 text-center">
            Enter your mobile number and passcode to continue
          </p>

          <form onSubmit={handleSubmit} className="w-full space-y-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-600 mb-2">Mobile Number</label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="+27 XX XXX XXXX"
                    required
                    className="pl-12 h-12 bg-white border-gray-200 focus:border-afriperx-gold text-gray-900 placeholder:text-gray-400"
                    data-testid="login-mobile-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-2">Passcode (4 digits)</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    name="passcode"
                    type="password"
                    value={formData.passcode}
                    onChange={handleChange}
                    placeholder="Enter 4-digit passcode"
                    maxLength={4}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    required
                    className="pl-12 h-12 bg-white border-gray-200 focus:border-afriperx-gold text-gray-900 placeholder:text-gray-400 tracking-widest text-lg"
                    data-testid="login-password-input"
                  />
                </div>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-gray-400">
                    Default passcode: Last 4 digits of your mobile number
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-xs text-afriperx-gold hover:underline font-medium"
                    data-testid="forgot-password-link"
                  >
                    Forgot passcode?
                  </button>
                </div>
              </div>
            </div>

            {/* Terms checkbox */}
            <div className="flex items-start gap-3 relative z-10">
              <Checkbox
                id="terms"
                checked={formData.acceptedTerms}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, acceptedTerms: checked }))}
                className="mt-1 border-gray-300 data-[state=checked]:bg-afriperx-green data-[state=checked]:border-afriperx-gold relative z-10"
                data-testid="login-terms-checkbox"
              />
              <label htmlFor="terms" className="text-sm text-gray-500 cursor-pointer">
                I accept the{' '}
                <button 
                  type="button"
                  onClick={() => navigate('/terms')}
                  className="text-afriperx-gold hover:underline font-medium"
                  data-testid="terms-link"
                >
                  Terms & Conditions
                </button>
                {' '}and{' '}
                <span className="text-afriperx-gold hover:underline">Privacy Policy</span>
              </label>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary h-12 text-base"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                'Continue'
              )}
            </Button>
          </form>
        </div>
      </div>

      {/* OTP Modal */}
      <Dialog open={showOtpModal} onOpenChange={setShowOtpModal}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md" aria-describedby="otp-description">
          <DialogHeader>
            <DialogTitle className="text-center">
              <CheckCircle2 className="w-12 h-12 text-afriperx-gold mx-auto mb-4" />
              <span className="font-syne text-2xl">Verify Your Identity</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="text-center py-4" id="otp-description">
            <p className="text-gray-500 mb-2">
              A 6-digit verification code has been sent to
            </p>
            {maskedEmail && (
              <p className="text-sm font-medium text-gray-700 mb-4">{maskedEmail}</p>
            )}

            {fallbackCode && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4" data-testid="login-fallback-code">
                <p className="text-xs text-amber-600 font-medium mb-1">Email could not be sent. Use this code:</p>
                <p className="text-xl font-mono font-bold text-amber-800 tracking-[0.3em]">{fallbackCode}</p>
              </div>
            )}

            {!fallbackCode && (
              <p className="text-xs text-green-600 bg-green-50 border border-green-200 rounded-lg p-2 mb-4">
                Check your inbox and spam folder for the code from AfriPerx.
              </p>
            )}

            <div className="flex justify-center mb-6">
              <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
                <InputOTPGroup className="gap-2">
                  <InputOTPSlot index={0} className="w-12 h-14 text-xl bg-white border-gray-200 text-gray-900" />
                  <InputOTPSlot index={1} className="w-12 h-14 text-xl bg-white border-gray-200 text-gray-900" />
                  <InputOTPSlot index={2} className="w-12 h-14 text-xl bg-white border-gray-200 text-gray-900" />
                  <InputOTPSlot index={3} className="w-12 h-14 text-xl bg-white border-gray-200 text-gray-900" />
                  <InputOTPSlot index={4} className="w-12 h-14 text-xl bg-white border-gray-200 text-gray-900" />
                  <InputOTPSlot index={5} className="w-12 h-14 text-xl bg-white border-gray-200 text-gray-900" />
                </InputOTPGroup>
              </InputOTP>
            </div>

            <Button
              onClick={handleVerifyOtp}
              disabled={loading || otp.length !== 6}
              className="w-full btn-primary"
              data-testid="verify-otp-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                'Verify & Continue'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
